/* eslint-disable @typescript-eslint/no-explicit-any */
import Image from "next/image";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { IoIosArrowRoundBack, IoMdAddCircleOutline } from "react-icons/io";
import { PersistGate } from "redux-persist/integration/react";

import { estimatePromoCode } from "../../api/promoCode/estimateCode";
import { validatePromoCode } from "../../api/promoCode/validateCode";
import { getRestaurantById } from "../../api/restaurants/getRestaurantById";
import { RestaurantItemType } from "../../api/types/restaurant.type";
import { SocialSignUpType } from "../../api/types/social.type";
import { labelToAnchor } from "../../api/utils/helpers/labelToAnchor";
import { translationsContent } from "../../localization/translations";
import { useAppSelector } from "../../redux/hooks";
import { useAppDispatch } from "../../redux/hooks";
import {
  addItemToCart,
  clearCart,
  CustomFormulaItem,
  deleteItemFromCart,
  FormulaItem,
  Item,
  removeBaseFromCartCustomFormula,
  removeBaseFromCartFormula,
  removeIngredientFromCartCustomFormula,
  removeIngredientFromCartFormula,
  removeSauceFromCartCustomFromula,
  removeSauceFromCartFormula,
  removeSide1FromCartFormula,
  removeSide2FromCartFormula,
  selectCart,
  setDiscountPrice,
} from "../../redux/slices/cartSlice";
import { selectRestaurant } from "../../redux/slices/restaurantSlice";
import { persistor } from "../../redux/store";
import { useLanguage } from "../../store/lang.context";
import { useLoader } from "../../store/loader.context";
import { useRestaurantChangePopup } from "../../store/restaurantChangePopup";
import { useUser } from "../../store/user.context";
import imageLoader from "../../utils/loader";
import { translate } from "../../utils/translate";
import Login from "../auth/Login";
import Signup from "../auth/Signup";
import SocialSignUp from "../auth/SocialSignup";
import { SubmitItemType } from "../auth/SocialSignup/Form/types/submit.type";
import { CartItemType } from "../Takeaway";
import DarkButton from "../ui/button/DarkButton";
import Button from "../ui/button/SimpleButton";
import Input from "../ui/input/SimpleInput";
import TextArea from "../ui/input/TextArea";
import RestaurantHoursList from "../ui/list/RestaurantHoursList";
import BlurModal from "../ui/modal/BlurModal";
import classes from "./cart.module.scss";
import CustomSaladItemCard from "./CustomSaladItem";
import { CutleryBanner } from "./CutleryBanner";
import DessertItemCard from "./DessertItem";
import Popup from "./ErrorPopup";
import FormulaItemCard from "./FormulaItem";
import SingleItem from "./Item";
import SignatureItem from "./SignatureSaladItem";

export type SubmitCartType = {
  wants_cutlery: SubmitItemType<boolean>;
  comment: SubmitItemType<string>;
  promoCode: SubmitItemType<string>;
};

const getQuantities = (items: Item[], num_of_items: number): boolean => {
  let quantity = 0;
  items.forEach((item) => (quantity += item.quantity));
  return quantity >= num_of_items;
};

const checkFormulaIsComplete = (formula: FormulaItem) => {
  let check = true;
  check = check && getQuantities(formula.bases, formula.formula.num_of_base);
  check =
    check &&
    getQuantities(formula.ingredients, formula.formula.num_of_ingredients);
  check = check && getQuantities(formula.sauces, formula.formula.num_of_sauce);
  if (formula.formula.sides.length >= 1)
    check =
      check &&
      (getQuantities(formula.sides1, 1) ||
        (formula.formula.sides[0].title.toLowerCase().includes("dessert") &&
          !!formula.desserts.length));
  if (formula.formula.sides.length == 2)
    check =
      check && (getQuantities(formula.sides2, 1) || !!formula.desserts.length);
  return check;
};

const Cart = () => {
  const cart = useAppSelector(selectCart);
  const restaurant = useAppSelector(selectRestaurant);

  const hasCutlery = useMemo(
    () =>
      !!cart.items.filter(
        (item) => item.ingredient.category.slug === "couverts"
      ).length,
    [cart.items]
  );

  const [notFoundIngredients, setNotFoundIngredients] = useState<string[]>([]);
  const [canProceed, setCanProceed] = useState(true);
  const [confirmRestaurantChoicePopup, setConfirmRestaurantChoicePopup] =
    useState(false);
  const [formulaNotCompletedModal, setFormulaNotCompletedModal] =
    useState(false);
  const [loginModalIsOpen, setLoginModalIsOpen] = useState(false);
  const [socialLoginModalIsOpen, setSocialLoginModalIsOpen] =
    useState<SocialSignUpType>({
      id: 0,
      open: false,
    });
  const [signupModalIsOpen, setSignupModalIsOpen] = useState(false);
  const [cartForm, setCartForm] = useState<SubmitCartType>({
    wants_cutlery: { value: hasCutlery, isValid: true },
    comment: { value: "", isValid: true },
    promoCode: { value: "", isValid: true },
  });
  const [timeUpdate, setTimeUpdate] = useState(false);
  const [promoCodeNotValid, setPromoCodeNotValid] = useState(false);

  const { user } = useUser();
  const { language } = useLanguage();
  const { setLoader } = useLoader();
  const { setPopupIsOpen } = useRestaurantChangePopup();

  const router = useRouter();

  const dispatch = useAppDispatch();

  const handleGoToCart = () => {
    if (
      cart.formulaItems.length === 0 &&
      cart.items.length === 0 &&
      cart.customFormulaItems.length === 0 &&
      cart.customDessertItems.length === 0
    ) {
      dispatch(clearCart());
    }
    router.push(`/carte/${labelToAnchor(restaurant.name)}/${restaurant.id}`);
  };

  const handleGoToFidelity = () => {
    if (!user) {
      setLoginModalIsOpen(true);
      return;
    }
    if (
      cart.formulaItems.length === 0 &&
      cart.items.length === 0 &&
      cart.customFormulaItems.length === 0 &&
      cart.customDessertItems.length === 0
    ) {
      dispatch(clearCart());
    }
    router.push(
      `/carte/${labelToAnchor(restaurant.name)}/${restaurant.id}/#fidelite`
    );
    localStorage.setItem("path", "avantages");
  };

  const checkEatfidFormulasAreCompleted = () => {
    let notModified = true;
    cart.formulaItems.forEach((formula) => {
      if (!checkFormulaIsComplete(formula)) {
        setCanProceed(false);
        notModified = false;
        return;
      }
    });
    cart.customFormulaItems.forEach((formula) => {
      if (!formula.completed) {
        setCanProceed(false);
        notModified = false;
        return;
      }
    });
    if (notModified) setCanProceed(true);
  };

  const handleTime = () => {
    if (restaurant.date === "dynamic") {
      if (restaurant.time) {
        const date = new Date();
        const time = date.getHours() * 100 + date.getMinutes() + 15;
        const optionTime =
          +restaurant.time.substring(0, 2) * 100 +
          +restaurant.time.substring(3, 5);
        if (time < optionTime) {
          return true;
        } else {
          return false;
        }
      }
    } else return true;
  };

  const removeItem = (
    item: Item,
    ID: CartItemType,
    formulaItem?: FormulaItem,
    customFormula?: CustomFormulaItem
  ) => {
    const quantity = item.quantity;
    for (let i = 0; i < quantity; i++) {
      switch (ID) {
        case "ITEMS":
          dispatch(deleteItemFromCart(item.ingredient));
          break;
        case "FORMULA":
          if (formulaItem)
            switch (item.ingredient.category.slug) {
              case "base":
                dispatch(
                  removeBaseFromCartFormula({
                    formula: formulaItem.formula,
                    id: formulaItem.orderId,
                    ingredient: item.ingredient,
                  })
                );
                break;
              case "ingredient":
                dispatch(
                  removeIngredientFromCartFormula({
                    formula: formulaItem.formula,
                    id: formulaItem.orderId,
                    ingredient: item.ingredient,
                  })
                );
                break;
              case "sauces":
                dispatch(
                  removeSauceFromCartFormula({
                    formula: formulaItem.formula,
                    id: formulaItem.orderId,
                    ingredient: item.ingredient,
                  })
                );
                break;
              case "boissons" || "dessert":
                dispatch(
                  removeSide1FromCartFormula({
                    formula: formulaItem.formula,
                    id: formulaItem.orderId,
                    ingredient: item.ingredient,
                  })
                );
                dispatch(
                  removeSide2FromCartFormula({
                    formula: formulaItem.formula,
                    id: formulaItem.orderId,
                    ingredient: item.ingredient,
                  })
                );
                break;
            }
          break;
        case "CUSTOM":
          if (customFormula)
            switch (item.ingredient.category.slug) {
              case "base":
                dispatch(
                  removeBaseFromCartCustomFormula({
                    id: customFormula.orderId,
                    ingredient: item.ingredient,
                  })
                );
                break;
              case "ingredient":
                dispatch(
                  removeIngredientFromCartCustomFormula({
                    id: customFormula.orderId,
                    ingredient: item.ingredient,
                  })
                );
                break;
              case "sauces":
                dispatch(
                  removeSauceFromCartCustomFromula({
                    id: customFormula.orderId,
                    ingredient: item.ingredient,
                  })
                );
                break;
            }
          break;
      }
    }
  };

  const checkAvailability = (
    restaurant: RestaurantItemType,
    ingredients: Item[],
    ID: CartItemType,
    formulaItem?: FormulaItem,
    customFormula?: CustomFormulaItem
  ) => {
    let flag = true;
    let notFoundAtAll = true;
    ingredients.forEach((item) => {
      notFoundAtAll = true;
      restaurant.ingredients.forEach((item2) => {
        if (item.ingredient.id === item2.id) notFoundAtAll = false;
        if (
          item.ingredient.id === item2.id &&
          !item2.available_at_this_restaurant
        ) {
          if (flag) flag = false;
          if (notFoundIngredients.indexOf(item2.title) === -1) {
            setNotFoundIngredients((prevState) => [...prevState, item2.title]);
          }
          if (formulaItem) removeItem(item, ID, formulaItem);
          else if (customFormula)
            removeItem(item, ID, undefined, customFormula);
          else removeItem(item, ID);
        }
      });
      if (notFoundAtAll) {
        if (notFoundIngredients.indexOf(item.ingredient.title) === -1) {
          setNotFoundIngredients((prevState) => [
            ...prevState,
            item.ingredient.title,
          ]);
        }
        if (formulaItem) removeItem(item, ID, formulaItem);
        else if (customFormula) removeItem(item, ID, undefined, customFormula);
        else removeItem(item, ID);
        if (flag) flag = false;
      }
    });
    return flag;
  };

  const checkIngredients = async () => {
    setLoader(true);
    let flag = true;
    const response = await getRestaurantById(restaurant.id);
    if (response.success) {
      const restaurants = response.data;
      if (restaurants) {
        flag = checkAvailability(restaurants, cart.items, "ITEMS") && flag;
        cart.formulaItems.forEach((formula) => {
          flag =
            checkAvailability(
              restaurants,
              formula.extraBases,
              "FORMULA",
              formula
            ) && flag;
          flag =
            checkAvailability(restaurants, formula.bases, "FORMULA", formula) &&
            flag;
          flag =
            checkAvailability(
              restaurants,
              formula.extraIngredients,
              "FORMULA",
              formula
            ) && flag;
          flag =
            checkAvailability(
              restaurants,
              formula.ingredients,
              "FORMULA",
              formula
            ) && flag;
          flag =
            checkAvailability(
              restaurants,
              formula.extraSauces,
              "FORMULA",
              formula
            ) && flag;
          flag =
            checkAvailability(
              restaurants,
              formula.sauces,
              "FORMULA",
              formula
            ) && flag;
          flag =
            checkAvailability(
              restaurants,
              formula.extraSides1,
              "FORMULA",
              formula
            ) && flag;
          flag =
            checkAvailability(
              restaurants,
              formula.sides1,
              "FORMULA",
              formula
            ) && flag;
          if (formula.sides2) {
            flag =
              checkAvailability(
                restaurants,
                formula.extraSides2,
                "FORMULA",
                formula
              ) && flag;
            flag =
              checkAvailability(
                restaurants,
                formula.sides2,
                "FORMULA",
                formula
              ) && flag;
          }
        });
        cart.customFormulaItems.forEach((formula) => {
          flag =
            checkAvailability(
              restaurants,
              formula.bases,
              "CUSTOM",
              undefined,
              formula
            ) && flag;
          flag =
            checkAvailability(
              restaurants,
              formula.ingredients,
              "CUSTOM",
              undefined,
              formula
            ) && flag;
          flag =
            checkAvailability(
              restaurants,
              formula.extraSauces,
              "CUSTOM",
              undefined,
              formula
            ) && flag;
          flag =
            checkAvailability(
              restaurants,
              formula.sauces,
              "CUSTOM",
              undefined,
              formula
            ) && flag;
        });
      }
    }
    setLoader(false);
    return flag;
  };

  const handleGoToConfirmation = async () => {
    checkEatfidFormulasAreCompleted();

    const availability = await checkIngredients();

    if (!availability) {
      return;
    }

    if (handleTime()) {
      if (user) {
        if (canProceed) setConfirmRestaurantChoicePopup(true);
        else setFormulaNotCompletedModal(true);
      } else {
        setLoginModalIsOpen(true);
      }
    } else {
      setTimeUpdate(true);
    }
  };

  const closePopup = () => {
    if (formulaNotCompletedModal) setFormulaNotCompletedModal(false);
    else if (timeUpdate) setTimeUpdate(false);
    else if (promoCodeNotValid) setPromoCodeNotValid(false);
  };

  const handleChange = (
    identifier: keyof SubmitCartType,
    value: string | boolean
  ) => {
    switch (identifier) {
      case "wants_cutlery":
        const wantsCutlery = value as boolean;
        setCartForm((prevState) => ({
          ...prevState,
          wants_cutlery: { value: wantsCutlery, isValid: true },
        }));
        localStorage.setItem(identifier, wantsCutlery ? "1" : "0");

        if (wantsCutlery) {
          const { cutleryIngredient } = restaurant;
          if (cutleryIngredient)
            dispatch(addItemToCart({ ingredient: cutleryIngredient }));
          return;
        }

        const cutleryItemIngredient = cart.items.find(
          (item) => item.ingredient.category.slug === "couverts"
        )?.ingredient;
        if (cutleryItemIngredient)
          dispatch(deleteItemFromCart(cutleryItemIngredient));

        break;
      case "comment":
        localStorage.setItem(identifier, value as string);
        break;
      case "promoCode":
        setCartForm((prevState) => ({
          ...prevState,
          promoCode: { value: value as string, isValid: true },
        }));
        break;
    }
  };

  const checkPromoCode = async () => {
    setLoader(true);
    const code = cartForm.promoCode.value;
    if (code.length > 0) {
      const valid = await validatePromoCode(code, restaurant.id);
      if (valid.success) {
        const estimate = await estimatePromoCode(
          code,
          +(cart.cartTotal / 100).toFixed(2)
        );
        if (estimate.success && estimate.data) {
          dispatch(
            setDiscountPrice({
              discount: estimate.data.discount,
              promoCode: code,
            })
          );
          setCartForm((prevState) => ({
            ...prevState,
            promoCode: {
              value: "",
              isValid: true,
            },
          }));
        }
      } else {
        if (valid.error) {
          setPromoCodeNotValid(true);
        }
      }
    }
    setLoader(false);
  };

  useEffect(() => {
    localStorage.setItem(
      "wants_cutlery",
      cartForm.wants_cutlery.value ? "1" : "0"
    );
    localStorage.setItem("comment", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    checkEatfidFormulasAreCompleted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  useEffect(() => {
    if (notFoundIngredients.length > 0) {
      setPopupIsOpen({
        isOpen: true,
        notAvailableIngredients: notFoundIngredients,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notFoundIngredients]);

  useEffect(() => {
    setCartForm((prevState) => ({
      ...prevState,
      wants_cutlery: { value: hasCutlery, isValid: true },
    }));
    localStorage.setItem("wants_cutlery", hasCutlery ? "1" : "0");
  }, [hasCutlery]);

  return (
    <PersistGate persistor={persistor} loading={<>Loading...</>}>
      <div className={classes.container}>
        <div className={classes.cartItems}>
          <div className={classes.header}>
            <div>
              <div className={classes.back} onClick={handleGoToCart}>
                <div className={classes.image}>
                  <IoIosArrowRoundBack />
                </div>
                <p>{translate(translationsContent, "back", language)}</p>
              </div>
              <h2>{translate(translationsContent, "cart", language)}</h2>
            </div>
          </div>
          {cart.items.length === 0 &&
            cart.formulaItems.length === 0 &&
            cart.customFormulaItems.length === 0 &&
            cart.signatures.length === 0 &&
            cart.customDessertItems.length === 0 && (
              <div>
                <h2>
                  {translate(translationsContent, "cartContent1", language)}
                </h2>
                <Button
                  label={`${translate(
                    translationsContent,
                    "cartButton1",
                    language
                  )}`}
                  onClick={handleGoToCart}
                />
              </div>
            )}
          <div className={classes.itemsList}>
            {cart.customFormulaItems.map((item, i) => (
              <CustomSaladItemCard key={i} item={item} id={i} />
            ))}
            {cart.customDessertItems.map((dessert, i) => (
              <DessertItemCard key={i} item={dessert} id={i} />
            ))}
            {cart.formulaItems.map((item, i) => (
              <FormulaItemCard
                key={i}
                formula={item}
                id={i}
                isEatfid={item.is_paid_with_eatfid_points}
                notSalad={item.notModifiable}
              />
            ))}
            {cart.items.map((item, i) => (
              <SingleItem
                key={i}
                item={item}
                isEatfid={item.is_paid_with_eatfid_points}
              />
            ))}
            {cart.signatures.map((signature, i) => (
              <SignatureItem key={i} item={signature} />
            ))}
            {restaurant.cutleryIngredient && (
              <CutleryBanner
                handleCheckboxChange={handleChange}
                hasCutlery={cartForm.wants_cutlery.value}
              />
            )}
            <div className={classes.recyclableImage}>
              <Image
                loader={imageLoader}
                src={"Recyclables.png"}
                alt={"recyclable"}
                height={150}
                width={700}
              />
            </div>
          </div>
        </div>
        <div className={classes.checkout}>
          <h3>{translate(translationsContent, "cartContent2", language)}</h3>
          <div className={classes.restaurantHoursList}>
            <RestaurantHoursList standalone={false} />
          </div>
          {/* <div className={classes.divider} /> */}
          <div className={classes.textArea}>
            <TextArea<SubmitCartType, "comment">
              label={`${translate(
                translationsContent,
                "cartMessageLabel",
                language
              )}`}
              identifier="comment"
              onChange={handleChange}
              placeholder={`${translate(
                translationsContent,
                "cartMessagePlaceholder",
                language
              )}`}
              isValid={true}
            />
          </div>
          {/* <div className={classes.divider} /> */}
          <div className={classes.promoCode}>
            <Input<SubmitCartType, "promoCode">
              label=""
              identifier="promoCode"
              onChange={handleChange}
              type="text"
              isValid={true}
              error={false}
              placeholder={`${translate(
                translationsContent,
                "cartPromoPlaceholder",
                language
              )}`}
            />
            <div
              className={`${classes.add} ${
                cartForm.promoCode.value.length === 0 && classes.disable
              }`}
              onClick={() => {
                checkPromoCode();
              }}
            >
              <IoMdAddCircleOutline style={{ height: "35px", width: "35px" }} />
            </div>
          </div>
          {/* <div className={classes.divider} /> */}
          <div className={classes.total}>
            <h3>{translate(translationsContent, "total", language)}</h3>
            <p>{(cart.cartTotal / 100).toFixed(2)} €</p>
          </div>
          <div className={classes.discount}>
            <p>{translate(translationsContent, "discount", language)}</p>
            <p>{cart.discount.toFixed(2)} €</p>
          </div>
          <div className={classes.total}>
            <h3>{translate(translationsContent, "newTotal", language)}</h3>
            <p>
              {cart.cartTotal > 0
                ? (cart.cartTotal / 100 - cart.discount).toFixed(2)
                : cart.cartTotal}{" "}
              €
            </p>
          </div>
          <div className={classes.buttons}>
            <div className={classes.button}>
              <Button
                label={`${translate(
                  translationsContent,
                  "cartButton2",
                  language
                )}`}
                onClick={handleGoToCart}
              />
            </div>
            <div className={classes.button}>
              <Button
                label={`${translate(
                  translationsContent,
                  "cartButton3",
                  language
                )}`}
                onClick={handleGoToFidelity}
              />
            </div>
            <div className={classes.button2}>
              <Button
                label={`${translate(
                  translationsContent,
                  "paymentBtnLabel1",
                  language
                )}`}
                disabled={
                  cart.items.length === 0 &&
                  cart.formulaItems.length === 0 &&
                  cart.customFormulaItems.length === 0 &&
                  cart.signatures.length === 0 &&
                  cart.customDessertItems.length === 0
                }
                onClick={() => {
                  handleGoToConfirmation();
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {loginModalIsOpen && (
        <Login
          setLoginIsOpen={setLoginModalIsOpen}
          setSignUpIsOpen={setSignupModalIsOpen}
          setSocialSignUp={setSocialLoginModalIsOpen}
        />
      )}
      {socialLoginModalIsOpen.open && (
        <SocialSignUp
          setSignUpIsOpen={setSignupModalIsOpen}
          setLoginIsOpen={setLoginModalIsOpen}
          socialSignUp={socialLoginModalIsOpen}
          setSocialSignUp={setSocialLoginModalIsOpen}
        />
      )}
      {signupModalIsOpen && (
        <Signup
          setLoginIsOpen={setLoginModalIsOpen}
          setSignUpIsOpen={setSignupModalIsOpen}
          setSocialSignUp={setSocialLoginModalIsOpen}
        />
      )}
      {(formulaNotCompletedModal || timeUpdate || promoCodeNotValid) && (
        <Popup
          onClose={closePopup}
          message={`${
            promoCodeNotValid
              ? translate(translationsContent, "promoCode", language)
              : ""
          } ${
            timeUpdate
              ? translate(translationsContent, "cartPopupMessage2", language)
              : ""
          } ${
            formulaNotCompletedModal
              ? translate(translationsContent, "cartPopupMessage1", language)
              : ""
          }`}
        />
      )}
      {confirmRestaurantChoicePopup && (
        <BlurModal
          modalIsOpen={confirmRestaurantChoicePopup}
          setModalIsOpen={setConfirmRestaurantChoicePopup}
        >
          <div className={classes.restaurantConfirmationContainer}>
            <p className={classes.restaurantConfirmationText}>
              {translate(
                translationsContent,
                "restaurantConfirmationText",
                language
              )}{" "}
              <b>{restaurant.name}</b>
            </p>

            <div className={classes.buttons}>
              <DarkButton
                label={`${translate(
                  translationsContent,
                  "annulerButton",
                  language
                )}`}
                onClick={() => setConfirmRestaurantChoicePopup(false)}
              />
              <Button
                label={`${translate(
                  translationsContent,
                  "validerButton",
                  language
                )}`}
                onClick={() => {
                  router.push("/payment/");
                }}
              />
            </div>
          </div>
        </BlurModal>
      )}
    </PersistGate>
  );
};

export default Cart;
